import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../../config/apiConfig';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import '../../styles/login.css';
import './ChangePasswordPage.css';
import { logError } from '../../utils/logger';

const ChangePasswordPage = () => {
  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  const { token, isAuthenticated } = useAuth();
  const { notify } = useNotifications();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated || !token) {
      navigate('/login', { replace: true, state: { from: '/change-password' } });
      return;
    }
    setReady(true);
  }, [isAuthenticated, token, navigate]);

  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const validate = useCallback(() => {
    const validationErrors = {};

    if (!form.currentPassword) {
      validationErrors.currentPassword = 'Ingresá tu contraseña actual';
    }
    if (!form.newPassword) {
      validationErrors.newPassword = 'La nueva contraseña es obligatoria';
    } else if (form.newPassword.length < 8) {
      validationErrors.newPassword = 'Mínimo 8 caracteres';
    }
    if (!form.confirmPassword) {
      validationErrors.confirmPassword = 'Confirmá la nueva contraseña';
    } else if (form.newPassword !== form.confirmPassword) {
      validationErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    return validationErrors;
  }, [form.currentPassword, form.newPassword, form.confirmPassword]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      const validationErrors = validate();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setErrors({});
      setLoading(true);

      try {
        const response = await fetch(buildApiUrl('/api/auth/change-password'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            currentPassword: form.currentPassword,
            newPassword: form.newPassword,
            confirmPassword: form.confirmPassword,
          }),
        });

        const data = await response.json().catch(() => ({}));

        if (response.ok) {
          notify({
            message: data.message || 'Contraseña actualizada correctamente.',
            type: 'success',
          });
          setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } else {
          const errorText =
            data?.error ||
            data?.message ||
            'No se pudo actualizar la contraseña. Intentá de nuevo.';
          setErrors({ submit: errorText });
          notify({
            message: errorText,
            type: 'error',
          });
        }
      } catch (error) {
        logError('[ChangePasswordPage]', error);
        setErrors({ submit: 'Error de conexión. Intentá nuevamente.' });
        notify({
          message: 'Error de conexión. Intentá nuevamente.',
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    },
    [form.currentPassword, form.newPassword, form.confirmPassword, notify, token, validate],
  );

  if (!ready) {
    return (
      <div className="login-page change-password-page">
        <div className="login-container">
          <div className="login-form-section">
            <div className="verifying-token">
              <div className="spinner" />
              <p>Cargando…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page change-password-page">
      <div className="login-container">
        <div className="login-form-section">
          <div className="login-form-header">
            <h2>Cambiar contraseña</h2>
            <p>
              Ingresá tu contraseña actual y elegí una nueva (mínimo 8 caracteres, mayúsculas,
              minúsculas y números).
            </p>
          </div>

          <form className="login-form" onSubmit={handleSubmit} noValidate>
            {errors.submit ? (
              <div className="auth-message error" role="alert">
                {errors.submit}
              </div>
            ) : null}

            <div className="form-group">
              <label htmlFor="currentPassword" className="form-label">
                <i className="fas fa-lock" aria-hidden="true" />
                Contraseña actual
              </label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={form.currentPassword}
                onChange={handleChange}
                className={`form-input${errors.currentPassword ? ' error' : ''}`}
                autoComplete="current-password"
                required
                autoFocus
              />
              {errors.currentPassword ? (
                <div className="form-error">{errors.currentPassword}</div>
              ) : null}
            </div>

            <div className="form-group">
              <label htmlFor="newPassword" className="form-label">
                <i className="fas fa-lock" aria-hidden="true" />
                Nueva contraseña
              </label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={form.newPassword}
                onChange={handleChange}
                className={`form-input${errors.newPassword ? ' error' : ''}`}
                autoComplete="new-password"
                required
              />
              {errors.newPassword ? <div className="form-error">{errors.newPassword}</div> : null}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                <i className="fas fa-lock" aria-hidden="true" />
                Confirmar nueva contraseña
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                className={`form-input${errors.confirmPassword ? ' error' : ''}`}
                autoComplete="new-password"
                required
              />
              {errors.confirmPassword ? (
                <div className="form-error">{errors.confirmPassword}</div>
              ) : null}
            </div>

            <button type="submit" className="login-submit" disabled={loading}>
              <span
                className="auth-loading"
                aria-hidden="true"
                style={{ display: loading ? 'inline-block' : 'none' }}
              />
              <i className="fas fa-check" aria-hidden="true" />
              {loading ? 'Guardando…' : 'Guardar contraseña'}
            </button>
          </form>

          <div className="register-link">
            <Link to="/">
              <i className="fas fa-arrow-left" />
              Volver al inicio
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
